import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Chip, useTheme } from '@mui/material'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import VerifiedIcon from '@mui/icons-material/Verified'
import BoltIcon from '@mui/icons-material/Bolt'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import NightsStayIcon from '@mui/icons-material/NightsStay'
import CoffeeIcon from '@mui/icons-material/Coffee'
import Text from '../Text'

const PROVIDER_LABELS = {
  'hiperapi-v1': 'WhatsApp Web',
  'hiperapi-v2': 'WhatsApp Web',
  'hiperapi-v3-cloud-official': 'API Oficial Meta'
}

function pickGreeting(hour) {
  if (hour >= 5 && hour < 12) return { label: 'Bom dia', icon: WbSunnyIcon }
  if (hour >= 12 && hour < 18) return { label: 'Boa tarde', icon: CoffeeIcon }
  return { label: 'Boa noite', icon: NightsStayIcon }
}

function firstWord(name) {
  if (!name) return null
  const cleaned = name.trim()
  if (!cleaned) return null
  return cleaned.split(/\s+/)[0]
}

function getInitials(name) {
  if (!name) return 'API'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'API'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function buildSubtitle({ provider, isAuthenticated, phone, brandName }) {
  const providerLabel = PROVIDER_LABELS[provider] || 'WhatsApp'
  if (!isAuthenticated) {
    return `Vamos conectar seu ${providerLabel}? Estou pronto pra te ajudar nessa.`
  }
  if (provider === 'hiperapi-v3-cloud-official') {
    return `Tudo certo por aqui. ${brandName ? `${brandName} ` : ''}seu número está rodando na infraestrutura oficial da Meta.`
  }
  return `Tudo certo por aqui. Seu número ${phone || ''} está conectado e enviando mensagens.`
}

function PreviewWelcomeHeader({ provider, isAuthenticated, phone, businessProfile, verifiedName, profilePicture }) {
  const theme = useTheme()
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(timer)
  }, [])

  const greeting = useMemo(() => pickGreeting(now.getHours()), [now])
  const GreetingIcon = greeting.icon

  const displayName = firstWord(verifiedName) || firstWord(businessProfile?.about)
  const fullName = verifiedName || businessProfile?.about
  const initials = getInitials(verifiedName || displayName || 'API')
  const subtitle = buildSubtitle({
    provider,
    isAuthenticated,
    phone,
    brandName: displayName
  })

  const isMeta = provider === 'hiperapi-v3-cloud-official'

  return (
    <div className="preview-card-enter mb-8 flex items-center gap-4 md:gap-6">
      <div className="relative">
        <Avatar
          src={profilePicture || undefined}
          sx={{
            width: 72,
            height: 72,
            backgroundColor: theme.palette.primary.dark,
            color: '#fff',
            fontSize: 22,
            fontWeight: 700,
            border: `3px solid ${theme.palette.background.paper}`,
            boxShadow: '0 6px 18px rgba(0,0,0,0.3)'
          }}
        >
          {initials}
        </Avatar>
        {isAuthenticated && (
          <span
            className="absolute bottom-0 right-0 w-[20px] h-[20px] rounded-full border-[3px] preview-pulse"
            style={{
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.background.default
            }}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <GreetingIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Text className="text-2xl md:text-3xl font-black tracking-tight">
            {greeting.label}{displayName ? `, ${displayName}` : ''}!
          </Text>
        </div>
        <Text className="text-sm md:text-base font-light opacity-80 mt-1 leading-snug">
          {subtitle}
        </Text>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {isAuthenticated ? (
            <Chip
              icon={<BoltIcon style={{ fontSize: 14 }} />}
              label="Online agora"
              size="small"
              sx={{
                backgroundColor: `${theme.palette.primary.main}1F`,
                color: theme.palette.primary.main,
                fontWeight: 700,
                '.MuiChip-icon': { color: theme.palette.primary.main }
              }}
            />
          ) : (
            <Chip
              label="Aguardando conexão"
              size="small"
              sx={{
                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                color: '#f59e0b',
                fontWeight: 700
              }}
            />
          )}
          {isMeta && isAuthenticated && (
            <Chip
              icon={<VerifiedIcon style={{ fontSize: 14 }} />}
              label="API Oficial Meta"
              size="small"
              sx={{
                backgroundColor: 'rgba(52, 165, 255, 0.15)',
                color: '#34a5ff',
                fontWeight: 700,
                '.MuiChip-icon': { color: '#34a5ff' }
              }}
            />
          )}
          {!isMeta && isAuthenticated && (
            <Chip
              icon={<WhatsAppIcon style={{ fontSize: 14 }} />}
              label="WhatsApp Web"
              size="small"
              sx={{
                backgroundColor: `${theme.palette.primary.main}10`,
                color: theme.palette.primary.main,
                fontWeight: 700,
                '.MuiChip-icon': { color: theme.palette.primary.main }
              }}
            />
          )}
          {fullName && fullName !== displayName && (
            <Text className="text-xs font-light opacity-70 ml-1">
              {fullName}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}

export default PreviewWelcomeHeader
